import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { showContentModal } from '../ui/Modal';
import { listReferenceEntries, listReferenceItems, referenceItemContentUrl } from '../../data/reference';
import { describeApiError } from '../../data/http';
import type { ReferenceEntryItemSummary } from '../../data/types';

const ITEM_PAGE = 24;

interface ImageCollectionPickerProps {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  /** The collection reference key the author bound via Default value (`{{@Key}}`, already resolved server-side). */
  referenceKey: string;
}

/** Resolves the `scope` a collection reference key lives in — needed to build a content URL for the current pick. */
function useCollectionScope(referenceKey: string): { scope: string | null; error: string | null } {
  const { t } = useTranslation();
  const [state, setState] = useState<{ referenceKey: string; scope: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    listReferenceEntries({ use: 'image' })
      .then((response) => {
        if (cancelled) return;
        const entry = (response.items ?? []).find((candidate) => candidate.key === referenceKey);
        if (!entry) {
          setError(t('wizard.collection.unavailable'));
          return;
        }
        setState({ referenceKey, scope: entry.scope });
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeApiError(err, t('wizard.collection.unavailable')));
      });

    return () => {
      cancelled = true;
    };
  }, [referenceKey, t]);

  return { scope: state?.referenceKey === referenceKey ? state.scope : null, error };
}

/**
 * Wizard-time picker for an open (author left "Specific image" unset) `collection`-sourced
 * Image property. A compact trigger — not a sprawling inline grid — opens a searchable
 * modal to browse the collection; picking an item closes the modal.
 */
export function ImageCollectionPicker({ value, onChange, invalid, referenceKey }: ImageCollectionPickerProps) {
  const { t } = useTranslation();
  const { scope, error } = useCollectionScope(referenceKey);
  const [broken, setBroken] = useState(false);

  const openPicker = () => {
    showContentModal({
      title: t('wizard.collection.pickTitle'),
      size: 'wide',
      body: (close) => (
        <ImageCollectionBrowser
          referenceKey={referenceKey}
          value={value}
          onPick={(id) => {
            onChange(id);
            close();
          }}
        />
      )
    });
  };

  if (error) {
    return (
      <p role="alert" className="mt-2 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    );
  }

  const hasSelection = value !== '' && scope !== null && !broken;

  return (
    <div className={`mt-2 flex items-center gap-3 rounded-lg border p-2 ${invalid ? 'border-destructive' : 'border-border'}`}>
      {hasSelection ? (
        <img
          src={referenceItemContentUrl(scope, referenceKey, value)}
          alt=""
          onError={() => setBroken(true)}
          className="size-14 shrink-0 rounded border border-border bg-muted object-cover"
        />
      ) : (
        <div className="flex size-14 shrink-0 items-center justify-center rounded border border-dashed border-border bg-muted text-muted-foreground">
          <PhotoIcon className="size-6" aria-hidden="true" />
        </div>
      )}

      <p className="flex-1 truncate text-sm text-muted-foreground">
        {value ? t('wizard.collection.chosen') : t('wizard.collection.noneChosen')}
      </p>

      <button
        type="button"
        onClick={openPicker}
        disabled={!scope}
        className="inline-flex shrink-0 items-center rounded border border-input bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
      >
        {value ? t('wizard.collection.change') : t('wizard.collection.choose')}
      </button>
    </div>
  );
}

interface Loaded {
  scope: string;
  items: ReferenceEntryItemSummary[];
  itemCount: number;
}

/** The modal's body: search box + grid + pagination, entirely self-contained (the modal is an imperative, one-shot mount). */
function ImageCollectionBrowser({
  referenceKey,
  value,
  onPick
}: {
  referenceKey: string;
  value: string;
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation();
  const searchId = useId();

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;

    listReferenceEntries({ use: 'image' })
      .then((response) => {
        if (cancelled) return null;
        const entry = (response.items ?? []).find((candidate) => candidate.key === referenceKey);
        if (!entry) {
          setError(t('wizard.collection.unavailable'));
          return null;
        }
        return listReferenceItems(entry.scope, referenceKey, 0, ITEM_PAGE).then((page) => ({ scope: entry.scope, page }));
      })
      .then((result) => {
        if (cancelled || !result) return;
        setLoaded({ scope: result.scope, items: result.page.items, itemCount: result.page.itemCount });
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeApiError(err, t('wizard.collection.unavailable')));
      });

    return () => {
      cancelled = true;
    };
  }, [referenceKey, t]);

  const loadMore = async () => {
    if (!loaded) return;
    setBusy(true);
    try {
      const page = await listReferenceItems(loaded.scope, referenceKey, loaded.items.length, ITEM_PAGE);
      setLoaded({ ...loaded, items: [...loaded.items, ...page.items], itemCount: page.itemCount });
    } catch (err: unknown) {
      setError(describeApiError(err, t('wizard.collection.unavailable')));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <p role="alert" className="border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    );
  }

  if (!loaded) {
    return (
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {t('wizard.collection.loading')}
      </p>
    );
  }

  const needle = query.trim().toLowerCase();
  const visible = needle ? loaded.items.filter((item) => item.fileName.toLowerCase().includes(needle)) : loaded.items;

  return (
    <div className="space-y-3">
      <label htmlFor={searchId} className="relative block">
        <span className="sr-only">{t('wizard.collection.searchLabel')}</span>
        <MagnifyingGlassIcon
          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          id={searchId}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('wizard.collection.searchPlaceholder')}
          className="w-full rounded-sm border border-input bg-surface py-2 pr-3 pl-9 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
        />
      </label>

      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {loaded.items.length === 0 ? t('wizard.collection.empty') : t('wizard.collection.noMatches')}
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {visible.map((item) => {
            const selected = item.id === value;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onPick(item.id)}
                  aria-pressed={selected}
                  className={`w-full rounded-lg border p-2 text-left transition ${
                    selected ? 'border-ring ring-2 ring-ring' : 'border-border hover:border-ring'
                  }`}
                >
                  <img
                    src={referenceItemContentUrl(loaded.scope, referenceKey, item.id)}
                    alt=""
                    loading="lazy"
                    className="aspect-video w-full rounded border border-border bg-muted object-cover"
                  />
                  <p className="mt-1 truncate text-xs text-foreground" title={item.fileName}>
                    {item.fileName}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!needle && loaded.items.length < loaded.itemCount ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void loadMore()}
          className="inline-flex items-center rounded border border-input bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
        >
          {busy ? t('common.loading') : t('reference.previewLoadMore')}
        </button>
      ) : null}
    </div>
  );
}
